import React, {
  Fragment, Suspense,
} from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import LoadingScreen from './components/Loading/LoadingScreen';
import DashboardLayout from './components/Navigation';

import Login from './views/Login';
import PasswordRecovery from './views/PasswordRecovery';
import PostRegister from './views/Register/PostRegister';
import Verification from './views/Verification';

import ClientAuthGuard from './components/Auth/ClientAuthGuard';
import {
  UserType,
  UserRole,
} from './constants/user_types';
import { ChildrenProps, RouteConfig } from './types';
import AccountListView from './views/Account';
import UpsertView from './views/Admin/Users/UpsertView';
import UserListView from './views/Admin/Users/UserListView';
import LayoutListView from './views/ClientDetail/Layout';
import WorkOrderListView from './views/ClientDetail/WorkOrder';
import CompanyListView from './views/Company';
import DevicesListView from './views/Devices';
import FarmsListView from './views/Farms';
import Unauthorized from './views/Unauthorized';
import VarietiesListView from './views/Varieties';

import AccountHome from './views/ClientDetail/AccountHome';
import AccountDevicesListView from './views/ClientDetail/Devices';
import SectorDetail from './views/ClientDetail/Layout/SectorDetail';
import ClientUserListView from './views/ClientDetail/Users';

import AlertsListView from './components/Highcharts/AlertsTab/navbarAlerts';
import DegreeDayConfig from './components/Highcharts/DegreeDayChart/DegreeDayConfig';
import Et0Config from './components/Highcharts/Et0Chart/Et0Config';
import IrrigationConfig from './components/Highcharts/IrrigationChart/IrrigationConfig';
import KcConfig from './components/Highcharts/KcChart/KcConfig';
import ColdHoursConfig from './components/Highcharts/LineCharts/ColdHoursChart/ColdHoursConfig';
import ChillPortionsConfig from './components/Highcharts/LineCharts/ChillPortionsChart/ChillPortionsConfig';
import HumidityTemperatureConfig from './components/Highcharts/LineCharts/HumidityTemperatureChart/HumidityTemperatureConfig';
import PlantsChartConfig from './components/Highcharts/LineCharts/PlantsChart/PlantsChartConfig';
import SalinityConfig from './components/Highcharts/LineCharts/SalinityChart/SalinityConfig';
import SoilMoistureConfig from './components/Highcharts/LineCharts/SoilMoistureChart/SoilMoistureConfig';
import SoilTemperatureConfig from './components/Highcharts/LineCharts/SoilTemperatureChart/SoilTemperatureConfig';
import StackedSoilMoistureConfig from './components/Highcharts/LineCharts/StackedSoilMoistureChart/StackedSoilMoistureConfig';
import RainfallConfig from './components/Highcharts/RainfallChart/RainfallConfig';
import RootsConfig from './components/Highcharts/RootsChart/RootsConfig';
import BiweeklyReportDetail from './views/ClientDetail/BiweeklyReport';
import ConversationsListView from './views/ClientDetail/Conversations';
import RiegoListView from './views/ClientDetail/Riego';
import RiegoSectorDetail from './views/ClientDetail/Riego/SectorDetail';
import PhElectricityConfig from './components/Highcharts/LineCharts/PhElectricityChart/PhElectricityConfig';
import WindConfig from './components/Highcharts/LineCharts/WindChart/WindConfig';
import RadiationConfig from './components/Highcharts/LineCharts/RadiationChart/RadiationConfig';
import NodesDetail from './views/ClientDetail/Layout/DevicesDetail/NodeDetail';
import SensorModelDetail from './views/ClientDetail/Layout/DevicesDetail/SensorModelDetail';
import DpvConfig from './components/Highcharts/LineCharts/DpvChart/DpvConfig';
import Et0IrrigationConfig from './components/Highcharts/Et0Irrigation/Et0IrrigationConfig';
import WeatherStationDetail from './views/ClientDetail/WeatherStationDetail';
import DocumentationView from './views/Documentation';

const routesConfig = [
  {
    id: 1,
    exact: true,
    path: '/',
    component: () => <Redirect to="/login" />,
  },
  {
    id: 2,
    exact: true,
    path: '/login',
    component: () => <Login />,
  },
  {
    id: 4,
    exact: true,
    path: '/user-verification/:token',
    component: () => <Verification />,
  },
  {
    id: 5,
    exact: true,
    path: '/reset-password/:token',
    component: () => <PasswordRecovery />,
  },
  {
    id: 6,
    exact: true,
    path: '/reset-password?token=:token',
    component: () => <Redirect to="/reset-password/:token" />,
  },
  {
    id: 7,
    exact: true,
    path: '/postRegister',
    component: () => <PostRegister />,
  },
  {
    id: 8,
    exact: true,
    path: '/sin-autorizacion',
    guard: (props: ChildrenProps) => <ClientAuthGuard {...props} />,
    component: () => <Unauthorized />,
  },
  {
    id: 10,
    path: '/usuarios',
    guard: (props: ChildrenProps) => <ClientAuthGuard {...props}
      admittedUserTypes={[UserType.CDTEC]}
      admittedUserRoles={[UserRole.ADMIN, UserRole.OPERATOR]} />,
    layout: DashboardLayout,
    routes: [
      {
        id: 11,
        exact: true,
        path: '/usuarios',
        component: () => <UserListView />,
      },
      {
        id: 12,
        exact: true,
        path: '/usuarios/crear',
        component: () => <UpsertView />,
      },
      {
        id: 13,
        exact: true,
        path: '/usuarios/:id/editar',
        component: () => <UpsertView />,
      },
    ],
  },
  {
    id: 20,
    path: '/cuentas',
    guard: (props: ChildrenProps) => <ClientAuthGuard {...props}
      admittedUserTypes={[UserType.CDTEC]} />,
    layout: DashboardLayout,
    routes: [
      {
        id: 21,
        exact: true,
        path: '/cuentas',
        component: () => <AccountListView />,
      },
    ],
  },
  {
    id: 30,
    path: '/empresas',
    guard: (props: ChildrenProps) => <ClientAuthGuard {...props}
      admittedUserTypes={[UserType.CDTEC]}
      admittedUserRoles={[UserRole.ADMIN, UserRole.OPERATOR]} />,
    layout: DashboardLayout,
    routes: [
      {
        id: 31,
        exact: true,
        path: '/empresas',
        component: () => <CompanyListView />,
      },
    ],
  },
  {
    id: 40,
    path: '/equipos',
    guard: (props: ChildrenProps) => <ClientAuthGuard {...props}
      admittedUserTypes={[UserType.CDTEC]}
      admittedUserRoles={[UserRole.ADMIN]} />,
    layout: DashboardLayout,
    routes: [
      {
        id: 41,
        exact: true,
        path: '/equipos',
        component: () => <DevicesListView />,
      },
    ],
  },
  {
    id: 45,
    path: '/campos',
    guard: (props: ChildrenProps) => <ClientAuthGuard {...props}
      admittedUserTypes={[UserType.CDTEC]}
      admittedUserRoles={[UserRole.ADMIN]} />,
    layout: DashboardLayout,
    routes: [
      {
        id: 46,
        exact: true,
        path: '/campos',
        component: () => <FarmsListView />,
      },
    ],
  },
  {
    id: 50,
    path: '/especies-variedades',
    guard: (props: ChildrenProps) => <ClientAuthGuard {...props}
      admittedUserTypes={[UserType.CDTEC]}
      admittedUserRoles={[UserRole.ADMIN, UserRole.OPERATOR, UserRole.AGRO]} />,
    layout: DashboardLayout,
    routes: [
      {
        id: 51,
        exact: true,
        path: '/especies-variedades',
        component: () => <VarietiesListView />,
      },
    ],
  },
  {
    id: 60,
    path: '/inicio',
    layout: DashboardLayout,
    guard: (props: ChildrenProps) => <ClientAuthGuard {...props} admittedUserTypes={[
      UserType.CDTEC, UserType.CLIENT, UserType.MULTI_ACCOUNT_CLIENT, UserType.CONSULTANT,
    ]} />,
    routes: [
      {
        id: 61,
        exact: true,
        path: '/inicio',
        component: () => <AccountHome />,
      },
    ],
  },
  {
    id: 70,
    path: '/monitoreo-equipos',
    layout: DashboardLayout,
    guard: (props: ChildrenProps) => <ClientAuthGuard {...props} admittedUserTypes={[
      UserType.CDTEC, UserType.CLIENT, UserType.MULTI_ACCOUNT_CLIENT, UserType.CONSULTANT,
    ]} />,
    routes: [
      {
        id: 71,
        exact: true,
        path: '/monitoreo-equipos',
        component: () => <AccountDevicesListView />,
      },
    ],
  },
  {
    id: 80,
    path: '/layout',
    guard: (props: ChildrenProps) => <
      ClientAuthGuard {...props} admittedUserTypes={[
        UserType.CDTEC, UserType.CLIENT, UserType.MULTI_ACCOUNT_CLIENT, UserType.CONSULTANT,
      ]} />,
    layout: DashboardLayout,
    routes: [
      {
        id: 81,
        exact: true,
        path: '/layout',
        guard: (props: ChildrenProps) => <
          ClientAuthGuard {...props} admittedUserTypes={[UserType.CDTEC]}
          admittedUserRoles={[UserRole.ADMIN, UserRole.OPERATOR]} />,
        component: () => <LayoutListView />,
      },
      {
        id: 85,
        exact: true,
        path: '/layout/nodos/:id',
        component: () => <NodesDetail />,
      },
      {
        id: 85,
        exact: true,
        path: '/layout/sensorModels/:id',
        component: () => <SensorModelDetail />,
      },
    ],
  },
  {
    id: 90,
    path: '/riego',
    guard: (props: ChildrenProps) => <
      ClientAuthGuard {...props} admittedUserTypes={[
        UserType.CDTEC, UserType.CLIENT, UserType.MULTI_ACCOUNT_CLIENT, UserType.CONSULTANT,
      ]} />,
    layout: DashboardLayout,
    routes: [
      {
        id: 91,
        exact: true,
        path: '/riego',
        component: () => <RiegoListView />,
      },
      {
        id: 92,
        exact: true,
        path: '/riego/:id',
        component: () => <RiegoSectorDetail />,
      },
    ],
  },
  {
    id: 100,
    path: '/conversaciones',
    layout: DashboardLayout,
    guard: (props: ChildrenProps) => <
      ClientAuthGuard {...props} admittedUserTypes={[
        UserType.CDTEC, UserType.CLIENT, UserType.MULTI_ACCOUNT_CLIENT, UserType.CONSULTANT,
      ]} />,
    routes: [
      {
        id: 101,
        exact: true,
        path: '/conversaciones',
        component: () => <ConversationsListView />,
      },
    ],
  },
  {
    id: 110,
    path: '/alertas',
    layout: DashboardLayout,
    guard: (props: ChildrenProps) => <
      ClientAuthGuard {...props} admittedUserTypes={[
        UserType.CDTEC, UserType.CLIENT, UserType.MULTI_ACCOUNT_CLIENT, UserType.CONSULTANT,
      ]} />,
    routes: [
      {
        id: 111,
        exact: true,
        path: '/alertas',
        component: () => <AlertsListView />,
      },
    ],
  },
  {
    id: 120,
    path: '/ordenes-de-trabajo',
    layout: DashboardLayout,
    guard: (props: ChildrenProps) => <
      ClientAuthGuard {...props} admittedUserTypes={[UserType.CDTEC]}
      admittedUserRoles={[UserRole.ADMIN, UserRole.OPERATOR]} />,
    routes: [
      {
        id: 121,
        exact: true,
        path: '/ordenes-de-trabajo',
        component: () => <WorkOrderListView />,
      },
    ],
  },
  {
    id: 140,
    path: '/cliente-usuarios',
    layout: DashboardLayout,
    guard: (props: ChildrenProps) => <
      ClientAuthGuard {...props} admittedUserTypes={[
        UserType.CDTEC, UserType.CLIENT, UserType.MULTI_ACCOUNT_CLIENT, UserType.CONSULTANT,
      ]} />,
    routes: [
      {
        id: 141,
        exact: true,
        path: '/cliente-usuarios',
        component: () => <ClientUserListView />,
      },
    ],
  },
  {
    id: 150,
    path: '/graficos/fullScreen',
    guard: (props: ChildrenProps) => <ClientAuthGuard {...props} admittedUserTypes={[
      UserType.CDTEC, UserType.CLIENT, UserType.MULTI_ACCOUNT_CLIENT, UserType.CONSULTANT]} />,
    routes: [
      {
        id: 171,
        exact: true,
        path: '/graficos/fullScreen/humedad-suelo/',
        component: () => <SoilMoistureConfig fullScreen />,
      },
      {
        id: 152,
        exact: true,
        path: '/graficos/fullScreen/humedad-suelo-apilada/',
        component: () => <StackedSoilMoistureConfig fullScreen />,
      },
      {
        id: 154,
        exact: true,
        path: '/graficos/fullScreen/salinidad-suelo-apilado/',
        component: () => <SalinityConfig fullScreen />,
      },
      {
        id: 155,
        exact: true,
        path: '/graficos/fullScreen/temperatura-suelo/',
        component: () => <SoilTemperatureConfig fullScreen />,
      },
      {
        id: 156,
        exact: true,
        path: '/graficos/fullScreen/riego/',
        component: () => <IrrigationConfig fullScreen />,
      },
      {
        id: 157,
        exact: true,
        path: '/graficos/fullScreen/temperatura-humedad-relativa/',
        component: () => <h1>In progress</h1>,
      },
      {
        id: 158,
        exact: true,
        path: '/graficos/fullScreen/horas-frio/',
        component: () => <ColdHoursConfig fullScreen />,
      },
      {
        id: 190,
        exact: true,
        path: '/graficos/fullScreen/porciones-frio/',
        component: () => <ChillPortionsConfig fullScreen />,
      },
      {
        id: 159,
        exact: true,
        path: '/graficos/fullScreen/grados-dia/',
        component: () => <DegreeDayConfig fullScreen />,
      },
      {
        id: 160,
        exact: true,
        path: '/graficos/fullScreen/precipitaciones/',
        component: () => <RainfallConfig fullScreen />,
      },
      {
        id: 161,
        exact: true,
        path: '/graficos/fullScreen/et0/',
        component: () => <Et0Config fullScreen />,
      },
      {
        id: 162,
        exact: true,
        path: '/graficos/fullScreen/viento/',
        component: () => <WindConfig fullScreen />,
      },
      {
        id: 163,
        exact: true,
        path: '/graficos/fullScreen/radiacion-solar/',
        component: () => <RadiationConfig fullScreen />,
      },
      {
        id: 164,
        exact: true,
        path: '/graficos/fullScreen/planta/',
        component: () => <PlantsChartConfig fullScreen />,
      },
      {
        id: 165,
        exact: true,
        path: '/graficos/fullScreen/kc/',
        component: () => <KcConfig fullScreen />,
      },
      {
        id: 166,
        exact: true,
        path: '/graficos/fullScreen/profundidad-riego/',
        component: () => <RootsConfig fullScreen />,
      },
      {
        id: 167,
        exact: true,
        path: '/graficos/fullScreen/temperatura-humedad/',
        component: () => <HumidityTemperatureConfig fullScreen />,
      },
      {
        id: 168,
        exact: true,
        path: '/graficos/fullScreen/ph-electricity/',
        component: () => <PhElectricityConfig fullScreen />,
      },
      {
        id: 169,
        exact: true,
        path: '/graficos/fullScreen/dpv/',
        component: () => <DpvConfig fullScreen />,
      },
      {
        id: 170,
        exact: true,
        path: '/graficos/fullScreen/et0Irrigation/',
        component: () => <Et0IrrigationConfig fullScreen />,
      },
    ],
  },
  {
    id: 150,
    path: '/graficos',
    layout: DashboardLayout,
    guard: (props: ChildrenProps) => <ClientAuthGuard {...props} admittedUserTypes={[
      UserType.CDTEC, UserType.CLIENT, UserType.MULTI_ACCOUNT_CLIENT, UserType.CONSULTANT]} />,
    routes: [
      {
        id: 151,
        exact: true,
        path: '/graficos/humedad-suelo/',
        component: () => <SoilMoistureConfig />,
      },
      {
        id: 152,
        exact: true,
        path: '/graficos/humedad-suelo-apilada/',
        component: () => <StackedSoilMoistureConfig />,
      },
      {
        id: 153,
        exact: true,
        path: '/graficos/cambio-total-negativo/',
        component: () => <h1>In progress</h1>,
      },
      {
        id: 154,
        exact: true,
        path: '/graficos/salinidad-suelo-apilado/',
        component: () => <SalinityConfig />,
      },
      {
        id: 155,
        exact: true,
        path: '/graficos/temperatura-suelo/',
        component: () => <SoilTemperatureConfig />,
      },
      {
        id: 156,
        exact: true,
        path: '/graficos/riego/',
        component: () => <IrrigationConfig />,
      },
      {
        id: 157,
        exact: true,
        path: '/graficos/temperatura-humedad-relativa/',
        component: () => <h1>In progress</h1>,
      },
      {
        id: 158,
        exact: true,
        path: '/graficos/horas-frio/',
        component: () => <ColdHoursConfig />,
      },
      {
        id: 190,
        exact: true,
        path: '/graficos/porciones-frio/',
        component: () => <ChillPortionsConfig />,
      },
      {
        id: 159,
        exact: true,
        path: '/graficos/grados-dia/',
        component: () => <DegreeDayConfig />,
      },
      {
        id: 160,
        exact: true,
        path: '/graficos/precipitaciones/',
        component: () => <RainfallConfig />,
      },
      {
        id: 161,
        exact: true,
        path: '/graficos/et0/',
        component: () => <Et0Config />,
      },
      {
        id: 162,
        exact: true,
        path: '/graficos/viento/',
        component: () => <WindConfig />,
      },
      {
        id: 163,
        exact: true,
        path: '/graficos/radiacion-solar/',
        component: () => <RadiationConfig />,
      },
      {
        id: 164,
        exact: true,
        path: '/graficos/planta/',
        component: () => <PlantsChartConfig />,
      },
      {
        id: 165,
        exact: true,
        path: '/graficos/kc/',
        component: () => <KcConfig />,
      },
      {
        id: 166,
        exact: true,
        path: '/graficos/profundidad-riego/',
        component: () => <RootsConfig />,
      },
      {
        id: 167,
        exact: true,
        path: '/graficos/temperatura-humedad/',
        component: () => <HumidityTemperatureConfig />,
      },
      {
        id: 168,
        exact: true,
        path: '/graficos/ph-electricity/',
        component: () => <PhElectricityConfig />,
      },
      {
        id: 169,
        exact: true,
        path: '/graficos/dpv/',
        component: () => <DpvConfig />,
      },
      {
        id: 170,
        exact: true,
        path: '/graficos/et0Irrigation/',
        component: () => <Et0IrrigationConfig />,
      },
    ],
  },
  {
    id: 170,
    path: '/sectores/:id',
    guard: (props: ChildrenProps) => <
      ClientAuthGuard {...props} admittedUserTypes={[
        UserType.CDTEC, UserType.CLIENT, UserType.MULTI_ACCOUNT_CLIENT, UserType.CONSULTANT]} />,
    layout: DashboardLayout,
    routes: [
      {
        id: 171,
        exact: true,
        path: '/sectores/:id',
        guard: (props: ChildrenProps) => <
      ClientAuthGuard {...props} admittedUserTypes={[
        UserType.CDTEC, UserType.CLIENT, UserType.MULTI_ACCOUNT_CLIENT, UserType.CONSULTANT]} />,
        component: () => <SectorDetail />,
      },
    ],
  },
  {
    id: 185,
    path: '/weather-station/:stationId',
    guard: (props: ChildrenProps) => <
      ClientAuthGuard {...props} admittedUserTypes={[
        UserType.CDTEC, UserType.CLIENT,
        UserType.MULTI_ACCOUNT_CLIENT, UserType.CONSULTANT]} />,
    layout: DashboardLayout,
    routes: [
      {
        id: 186,
        exact: true,
        path: '/weather-station/:stationId',
        component: () => <WeatherStationDetail />,
      },
    ],
  },
  {
    id: 187,
    path: '/weather-station',
    guard: (props: ChildrenProps) => <
      ClientAuthGuard {...props} admittedUserTypes={[
        UserType.CDTEC, UserType.CLIENT,
        UserType.MULTI_ACCOUNT_CLIENT, UserType.CONSULTANT]} />,
    layout: DashboardLayout,
    routes: [
      {
        id: 188,
        exact: true,
        path: '/weather-station',
        component: () => <WeatherStationDetail />,
      },
    ],
  },
  {
    id: 190,
    path: '/documentacion',
    guard: (props: ChildrenProps) => <ClientAuthGuard {...props}
      admittedUserTypes={[UserType.CDTEC]}
      admittedUserRoles={[UserRole.ADMIN]} />,
    layout: DashboardLayout,
    routes: [
      {
        id: 191,
        exact: true,
        path: '/documentacion',
        component: () => <DocumentationView />,
      },
    ],
  },
  {
    id: 180,
    path: '/informe-quincenal/:reportId',
    guard: (props: ChildrenProps) => <
      ClientAuthGuard {...props} admittedUserTypes={[
        UserType.CDTEC, UserType.CLIENT, UserType.MULTI_ACCOUNT_CLIENT, UserType.CONSULTANT]} />,
    layout: DashboardLayout,
    routes: [
      {
        id: 181,
        exact: true,
        path: '/informe-quincenal/:reportId',
        component: () => <BiweeklyReportDetail />,
      },
    ],
  },
];

const renderRoutes = (routes: RouteConfig[]) => (routes ? (
  <Suspense fallback={<LoadingScreen />}>
    <Switch>
      {routes.map((route: RouteConfig, i) => {
        const Guard = route.guard || Fragment;
        const Layout = route.layout || Fragment;
        const Component = route.component as React.ElementType;

        return (
          <Route
            key={i}
            path={route.path}
            exact={route.exact}
            render={(props) => (
              <Guard>
                <Layout>
                  {route.routes ? (
                    renderRoutes(route.routes)
                  ) : (
                    <Component {...props} />
                  )}
                </Layout>
              </Guard>
            )}
          />
        );
      })}
      <Redirect to="/login" />
    </Switch>
  </Suspense>
) : null);

function Routes() {
  return renderRoutes(routesConfig);
}

export default Routes;
